import type { Money } from './contract';
import { OrderCouponDetails } from './OrderCouponDetails';
import type { CheckoutOrderCoupon, CheckoutOrderSummary } from './api';
import { formatMoney, sumMoney } from './money';
import { CheckIcon } from './icons';
import { tr } from '../i18n';

export interface ConfirmationLine {
  id: string;
  couponCode?: string | undefined;
  coupon?: CheckoutOrderCoupon | undefined;
  title: string;
  amount: Money | null;
  amountLabel: string;
  statusLabel: string;
  items?: readonly { id: string; title: string; amountLabel: string }[] | undefined;
  categoryLabel?: string | undefined;
  methodLabel?: string | undefined;
  renewalLabel?: string | undefined;
  recurring?: boolean | undefined;
  paid?: boolean | undefined;
  sourceKind?: CheckoutOrderSummary['charges'][number]['kind'] | 'bump' | undefined;
}

export function corePaymentMethodLabel(method: CheckoutOrderSummary['charges'][number]['paymentMethod']): string | undefined {
  if (method === 'PIX') {
    return tr('payment.pix');
  }
  if (method === 'BOLETO') {
    return tr('payment.boleto');
  }
  if (method === 'CREDIT_CARD') {
    return tr('payment.card');
  }
  if (method === 'APPLE_PAY') {
    return tr('payment.applePay');
  }
  if (method === 'GOOGLE_PAY') {
    return tr('payment.googlePay');
  }
  return undefined;
}

export function chargeStatusLabel(status: CheckoutOrderSummary['charges'][number]['status']): string {
  if (status === 'REFUND_REQUESTED') return tr('payment.refundRequested');
  if (status === 'PARTIALLY_REFUNDED') return tr('payment.partiallyRefunded');
  if (status === 'REFUNDED') return tr('payment.refunded');
  if (status === 'CHARGED_BACK') return tr('payment.disputed');
  if (status === 'PAID') {
    return tr('payment.approved');
  }
  if (status === 'PENDING') {
    return tr('payment.pending');
  }
  if (status === 'REQUIRES_ACTION') {
    return tr('payment.requiresAction');
  }
  return tr('payment.notConfirmed');
}

export function totalLabelFromLines(lines: readonly ConfirmationLine[]): string | null {
  const byCurrency = new Map<string, Money[]>();
  for (const line of lines) {
    if (line.paid === false) continue;
    if (line.amount === null) continue;
    byCurrency.set(line.amount.currency, [...(byCurrency.get(line.amount.currency) ?? []), line.amount]);
  }
  if (byCurrency.size === 0) return null;
  try {
    const labels = [...byCurrency.values()]
      .map((amounts) => {
        const total = sumMoney(amounts);
        return total === null ? null : formatMoney(total);
      })
      .filter((label): label is string => label !== null);
    return labels.length > 0 ? labels.join(' + ') : null;
  } catch {
    return null;
  }
}

interface PaymentConfirmationProps {
  email?: string | undefined;
  isMock?: boolean;
  paymentChanged?: boolean;
  currentPaymentLabel: string;
  paidNowLines: readonly ConfirmationLine[];
  paidNowDisplayLabel: string;
  pendingConfirmationLines: readonly ConfirmationLine[];
  recurringLines: readonly ConfirmationLine[];
}

export function PaymentConfirmation({ email, isMock, paymentChanged, currentPaymentLabel, paidNowLines, paidNowDisplayLabel, pendingConfirmationLines, recurringLines }: PaymentConfirmationProps) {
  return (
        <section className="vp-card vp-success vp-success--wide">
          {isMock && <span className="vp-demo-badge">{tr('common.demo')}</span>}
          {!paymentChanged && <div className="vp-success-mark" aria-hidden="true">
            <CheckIcon width={36} height={36} />
          </div>}
          <h1>{tr(paymentChanged ? 'checkout.orderSummary' : 'success.paymentTitle')}</h1>
          {!paymentChanged && <p>
            {tr('checkout.confirmationSent', {
              email: email || tr('common.emailFallback'),
              delivery: tr('checkout.productDelivery'),
            })}
          </p>}
          <div className="vp-payment-method">
            <span>{tr('payment.method')}</span>
            <strong>{currentPaymentLabel}</strong>
          </div>
          {(!paymentChanged || paidNowLines.length > 0) && <div className="vp-confirmation-list" aria-label={tr('checkout.reviewSummary')}>
            <div className="vp-confirmation-heading">{tr('checkout.chargedNow')}</div>
            {paidNowLines.map((line) => (
              <div className="vp-confirmation-line" key={line.id}>
                <span>
                  <strong>
                    {line.title}
                    {line.categoryLabel && <small className="vp-confirmation-tag">{line.categoryLabel}</small>}
                  </strong>
                  <em>
                    {line.statusLabel}
                    {line.methodLabel ? ` · ${line.methodLabel}` : ''}
                  </em>
                  {!line.coupon && line.couponCode && <em className="vp-confirmation-coupon">{tr('coupon.label', { code: line.couponCode })}</em>}
                  {!line.coupon && line.items?.map((item) => <em key={item.id}>{item.title} · {item.amountLabel}</em>)}
                  {line.renewalLabel && <em className="vp-confirmation-renewal">{tr('checkout.renewsFor', { value: line.renewalLabel })}</em>}
                </span>
                {!(line.coupon && line.items?.length) && <strong className="vp-num">{line.amountLabel}</strong>}
                {line.coupon && <OrderCouponDetails coupon={line.coupon} productTitle={line.title} />}
                {line.coupon && Boolean(line.items?.length) && (
                  <div className="vp-confirmation-composition">
                    <h2>{tr('summary.purchaseItems')}</h2>
                    <dl>
                      {line.items?.map((item) => <div key={item.id}><dt>{item.title}</dt><dd className="vp-num">{item.amountLabel}</dd></div>)}
                    </dl>
                    <div className="vp-confirmation-subtotal"><strong>{tr('summary.purchaseTotal')}</strong><strong className="vp-num">{line.amountLabel}</strong></div>
                  </div>
                )}
              </div>
            ))}
            <div className="vp-confirmation-total">
              <span>{tr('checkout.totalChargedNow')}</span>
              <strong className="vp-num">{paidNowDisplayLabel}</strong>
            </div>
          </div>}
          {pendingConfirmationLines.length > 0 && (
            <div className="vp-confirmation-list" aria-label={tr(paymentChanged ? 'payment.updatedCharges' : 'checkout.awaitingConfirmation')}>
              <div className="vp-confirmation-heading">{tr(paymentChanged ? 'payment.updatedCharges' : 'checkout.awaitingConfirmation')}</div>
              {pendingConfirmationLines.map((line) => (
                <div className="vp-confirmation-line" key={line.id}>
                  <span>
                    <strong>
                      {line.title}
                      {line.categoryLabel && <small className="vp-confirmation-tag">{line.categoryLabel}</small>}
                    </strong>
                    <em>
                      {line.statusLabel}
                      {line.methodLabel ? ` · ${line.methodLabel}` : ''}
                    </em>
                    {line.items?.map((item) => <em key={item.id}>{item.title} · {item.amountLabel}</em>)}
                  {!paymentChanged && line.renewalLabel && <em className="vp-confirmation-renewal">{tr('checkout.renewsFor', { value: line.renewalLabel })}</em>}
                  </span>
                  <strong className="vp-num">{line.amountLabel}</strong>
                </div>
              ))}
            </div>
          )}
          {recurringLines.length > 0 && (
            <div className="vp-confirmation-list vp-confirmation-list--recurring" aria-label={tr('checkout.recurringCharges')}>
              <div className="vp-confirmation-heading">{tr('checkout.nextCharges')}</div>
              {recurringLines.map((line) => (
                <div className="vp-confirmation-line" key={line.id}>
                  <span>
                    <strong>{line.title}</strong>
                    <em>{tr('checkout.subscriptionActive')}</em>
                  </span>
                  <strong className="vp-num">{line.renewalLabel ?? line.amountLabel}</strong>
                </div>
              ))}
            </div>
          )}
          {isMock && (
            <p className="vp-faint vp-small">{tr('checkout.demoNotice')}</p>
          )}
        </section>
  );
}
